import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../components/ui/sonner';
import {
  Download, FileSignature, LogOut, Package, Send, ShieldCheck, Truck, UserPlus, Users,
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { getErrorMessage } from '../lib/errors';
import { clearAdminToken } from '../lib/authTokens';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const ORDER_STATUSES = ['Submitted', 'Quoted', 'Confirmed', 'Shipped', 'Delivered'];

const exportCSV = (rows, filename) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]).filter((k) => k !== 'id');
  const escapeCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [headers.join(','), ...rows.map((r) => headers.map((h) => escapeCell(r[h])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const emptySupplierForm = { name: '', company: '', email: '', phone: '', type: 'supplier', address: '' };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [leads, setLeads] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [supplierForm, setSupplierForm] = useState(emptySupplierForm);
  const [savingSupplier, setSavingSupplier] = useState(false);

  const [ncaDialogSupplier, setNcaDialogSupplier] = useState(null);
  const [ncaDocType, setNcaDocType] = useState('supplier_nca');
  const [sendingNca, setSendingNca] = useState(false);

  const [requestDialogOrder, setRequestDialogOrder] = useState(null);
  const [requestSupplierId, setRequestSupplierId] = useState('');
  const [sendingRequest, setSendingRequest] = useState(false);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/orders`, { withCredentials: true }),
      axios.get(`${API}/admin/leads`, { withCredentials: true }),
      axios.get(`${API}/admin/suppliers`, { withCredentials: true }),
      axios.get(`${API}/admin/agreements`, { withCredentials: true }),
      axios.get(`${API}/admin/supplier-requests`, { withCredentials: true }),
    ])
      .then(([ordersRes, leadsRes, suppliersRes, agreementsRes, requestsRes]) => {
        setOrders(ordersRes.data);
        setLeads(leadsRes.data);
        setSuppliers(suppliersRes.data);
        setAgreements(agreementsRes.data);
        setRequests(requestsRes.data);
      })
      .catch(() => navigate('/admin/login'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    clearAdminToken();
    navigate('/admin/login');
  };

  const handleAddSupplier = async (e) => {
    e.preventDefault();
    if (!supplierForm.name || !supplierForm.email || !supplierForm.phone) {
      toast.error('Name, email and phone are required');
      return;
    }
    setSavingSupplier(true);
    try {
      const res = await axios.post(`${API}/admin/suppliers`, supplierForm, { withCredentials: true });
      setSuppliers((prev) => [res.data, ...prev]);
      setSupplierDialogOpen(false);
      setSupplierForm(emptySupplierForm);
      toast.success(`${res.data.name} added`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to add supplier/agent'));
    } finally {
      setSavingSupplier(false);
    }
  };

  const handleSendNca = async () => {
    setSendingNca(true);
    try {
      const res = await axios.post(
        `${API}/admin/agreements`,
        { supplier_id: ncaDialogSupplier.id, document_type: ncaDocType, frontend_base_url: window.location.origin },
        { withCredentials: true }
      );
      setAgreements((prev) => [res.data, ...prev]);
      setNcaDialogSupplier(null);
      toast.success(`Agreement sent to ${ncaDialogSupplier.email}`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to send agreement'));
    } finally {
      setSendingNca(false);
    }
  };

  const handleSendSupplierRequest = async () => {
    if (!requestSupplierId) {
      toast.error('Please select a supplier');
      return;
    }
    setSendingRequest(true);
    try {
      const res = await axios.post(
        `${API}/admin/supplier-requests`,
        { order_number: requestDialogOrder.order_number, supplier_id: requestSupplierId },
        { withCredentials: true }
      );
      setRequests((prev) => [res.data, ...prev]);
      setRequestDialogOrder(null);
      setRequestSupplierId('');
      toast.success(`Quote request sent to ${res.data.supplier_name}`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to send supplier request'));
    } finally {
      setSendingRequest(false);
    }
  };

  const copySignLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/sign/${token}`);
    toast.success('Signing link copied to clipboard');
  };

  const handleStatusChange = async (orderNumber, status) => {
    try {
      await axios.patch(`${API}/admin/orders/${orderNumber}/status`, { status }, { withCredentials: true });
      setOrders((prev) => prev.map((o) => (o.order_number === orderNumber ? { ...o, status } : o)));
      toast.success(`Order ${orderNumber} marked ${status}`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update status'));
    }
  };

  const supplierOptions = suppliers.filter((s) => s.type === 'supplier');

  return (
    <div className="min-h-screen bg-brand-bg text-brand-text font-body">
      <header className="flex items-center justify-between px-5 sm:px-8 h-20 border-b border-brand-border/60">
        <Logo />
        <div className="flex items-center gap-4">
          <a
            href="/admin/quotes"
            data-testid="admin-dashboard-quotes-link"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-brand-amber border border-brand-border rounded-full px-4 py-2 hover:border-brand-amber transition-colors"
          >
            <Send className="w-3.5 h-3.5" />
            Wholesale Quotes
          </a>
          <button
            type="button"
            data-testid="admin-logout-button"
            onClick={handleLogout}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-brand-muted border border-brand-border rounded-full px-4 py-2 hover:border-brand-orange hover:text-brand-orange transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10" data-testid="admin-dashboard">
        <h1 className="font-display font-extrabold uppercase text-3xl text-brand-text">Admin Dashboard</h1>
        <p className="mt-2 text-brand-muted text-sm">Bulk orders, supplier network, and agreements — newest first.</p>

        {loading ? (
          <p className="mt-10 font-mono text-sm text-brand-muted">Loading…</p>
        ) : (
          <Tabs defaultValue="orders" className="mt-8">
            <TabsList className="bg-brand-surface2 border border-brand-border p-1 rounded-lg h-auto flex-wrap">
              <TabsTrigger value="orders" data-testid="admin-tab-orders" className="font-mono text-xs uppercase tracking-wider text-brand-muted data-[state=active]:bg-brand-orange data-[state=active]:text-white rounded-md px-4 py-2 flex items-center gap-1.5">
                <Package className="w-3.5 h-3.5" />
                Orders ({orders.length})
              </TabsTrigger>
              <TabsTrigger value="requests" data-testid="admin-tab-requests" className="font-mono text-xs uppercase tracking-wider text-brand-muted data-[state=active]:bg-brand-orange data-[state=active]:text-white rounded-md px-4 py-2 flex items-center gap-1.5">
                <Truck className="w-3.5 h-3.5" />
                Supplier Requests ({requests.length})
              </TabsTrigger>
              <TabsTrigger value="suppliers" data-testid="admin-tab-suppliers" className="font-mono text-xs uppercase tracking-wider text-brand-muted data-[state=active]:bg-brand-orange data-[state=active]:text-white rounded-md px-4 py-2 flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" />
                Suppliers ({suppliers.length})
              </TabsTrigger>
              <TabsTrigger value="agreements" data-testid="admin-tab-agreements" className="font-mono text-xs uppercase tracking-wider text-brand-muted data-[state=active]:bg-brand-orange data-[state=active]:text-white rounded-md px-4 py-2 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" />
                Agreements ({agreements.length})
              </TabsTrigger>
              <TabsTrigger value="leads" data-testid="admin-tab-leads" className="font-mono text-xs uppercase tracking-wider text-brand-muted data-[state=active]:bg-brand-orange data-[state=active]:text-white rounded-md px-4 py-2 flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" />
                Leads ({leads.length})
              </TabsTrigger>
            </TabsList>

            {/* ORDERS */}
            <TabsContent value="orders" className="mt-6">
              <div className="flex justify-end mb-3">
                <button type="button" data-testid="admin-export-orders-button" onClick={() => exportCSV(orders, `bulkusedtires-orders-${Date.now()}.csv`)} disabled={orders.length === 0} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-brand-muted border border-brand-border rounded-full px-4 py-2 hover:border-brand-amber hover:text-brand-amber transition-colors disabled:opacity-40">
                  <Download className="w-3.5 h-3.5" />
                  Export CSV
                </button>
              </div>
              <div className="rounded-xl border border-brand-border overflow-hidden" data-testid="admin-orders-table">
                <Table>
                  <TableHeader>
                    <TableRow className="border-brand-border hover:bg-transparent">
                      {['Order #', 'Customer', 'Business', 'Tier', 'Contact', 'What They Need', 'Delivery Area', 'Account', 'Status', 'PDF?', 'Date', ''].map((h) => (
                        <TableHead key={h} className="text-brand-muted font-mono text-[11px] uppercase tracking-wider">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.length === 0 && (
                      <TableRow className="border-brand-border"><TableCell colSpan={12} className="text-center text-brand-muted py-8">No orders yet.</TableCell></TableRow>
                    )}
                    {orders.map((o) => (
                      <TableRow key={o.order_number} className="border-brand-border hover:bg-brand-surface2/60">
                        <TableCell className="font-mono text-brand-amber text-xs">{o.order_number}</TableCell>
                        <TableCell className="text-brand-text text-sm">{o.name}</TableCell>
                        <TableCell className="text-brand-muted text-sm">{o.business_name || '—'}</TableCell>
                        <TableCell className="text-brand-text text-sm">{o.tier}</TableCell>
                        <TableCell className="text-brand-muted text-xs">{o.email}<br />{o.phone}</TableCell>
                        <TableCell className="text-brand-muted text-xs max-w-xs">{o.request_details || '—'}</TableCell>
                        <TableCell className="text-brand-muted text-xs">{[o.delivery_city, o.delivery_state, o.delivery_country].filter(Boolean).join(', ') || '—'}</TableCell>
                        <TableCell className="text-brand-muted text-xs">{o.client_id ? 'Client' : 'Guest'}</TableCell>
                        <TableCell>
                          <Select value={o.status || 'Submitted'} onValueChange={(v) => handleStatusChange(o.order_number, v)}>
                            <SelectTrigger data-testid={`admin-order-status-${o.order_number}`} className="h-8 text-xs bg-brand-surface2 border-brand-border text-brand-text">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ORDER_STATUSES.map((s) => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-brand-muted text-xs">{o.wants_pdf ? 'Yes' : 'No'}</TableCell>
                        <TableCell className="text-brand-muted text-xs">{fmtDate(o.created_at)}</TableCell>
                        <TableCell>
                          <button
                            type="button"
                            data-testid={`admin-order-send-supplier-${o.order_number}`}
                            onClick={() => setRequestDialogOrder(o)}
                            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-brand-amber border border-brand-border rounded-full px-3 py-1.5 hover:border-brand-amber transition-colors whitespace-nowrap"
                          >
                            <Send className="w-3 h-3" />
                            Send to Supplier
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* SUPPLIER REQUESTS */}
            <TabsContent value="requests" className="mt-6">
              <div className="rounded-xl border border-brand-border overflow-hidden" data-testid="admin-requests-table">
                <Table>
                  <TableHeader>
                    <TableRow className="border-brand-border hover:bg-transparent">
                      {['Order #', 'Supplier', 'Tier', 'What They Need', 'Delivery Area', 'Status', 'Date'].map((h) => (
                        <TableHead key={h} className="text-brand-muted font-mono text-[11px] uppercase tracking-wider">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.length === 0 && (
                      <TableRow className="border-brand-border"><TableCell colSpan={7} className="text-center text-brand-muted py-8">No supplier requests sent yet.</TableCell></TableRow>
                    )}
                    {requests.map((r) => (
                      <TableRow key={`${r.order_number}-${r.created_at}`} className="border-brand-border hover:bg-brand-surface2/60">
                        <TableCell className="font-mono text-brand-amber text-xs">{r.order_number}</TableCell>
                        <TableCell className="text-brand-text text-sm">{r.supplier_name}</TableCell>
                        <TableCell className="text-brand-text text-sm">{r.tier}</TableCell>
                        <TableCell className="text-brand-muted text-xs max-w-xs">{r.request_details || '—'}</TableCell>
                        <TableCell className="text-brand-muted text-xs">{[r.delivery_city, r.delivery_state].filter(Boolean).join(', ') || '—'}</TableCell>
                        <TableCell className="text-brand-green text-xs uppercase">{r.status}</TableCell>
                        <TableCell className="text-brand-muted text-xs">{fmtDate(r.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* SUPPLIERS */}
            <TabsContent value="suppliers" className="mt-6">
              <div className="flex justify-end mb-3">
                <button type="button" data-testid="admin-add-supplier-button" onClick={() => setSupplierDialogOpen(true)} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-white bg-brand-orange rounded-full px-4 py-2 hover:bg-brand-orangeHover transition-colors">
                  <UserPlus className="w-3.5 h-3.5" />
                  Add Supplier / Agent
                </button>
              </div>
              <div className="rounded-xl border border-brand-border overflow-hidden" data-testid="admin-suppliers-table">
                <Table>
                  <TableHeader>
                    <TableRow className="border-brand-border hover:bg-transparent">
                      {['Name', 'Company', 'Type', 'Email', 'Phone', 'Address', ''].map((h) => (
                        <TableHead key={h} className="text-brand-muted font-mono text-[11px] uppercase tracking-wider">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {suppliers.length === 0 && (
                      <TableRow className="border-brand-border"><TableCell colSpan={7} className="text-center text-brand-muted py-8">No suppliers or agents on file yet.</TableCell></TableRow>
                    )}
                    {suppliers.map((s) => (
                      <TableRow key={s.id} className="border-brand-border hover:bg-brand-surface2/60">
                        <TableCell className="text-brand-text text-sm">{s.name}</TableCell>
                        <TableCell className="text-brand-muted text-sm">{s.company || '—'}</TableCell>
                        <TableCell className="text-brand-muted text-xs capitalize">{s.type}</TableCell>
                        <TableCell className="text-brand-muted text-xs">{s.email}</TableCell>
                        <TableCell className="text-brand-muted text-xs">{s.phone}</TableCell>
                        <TableCell className="text-brand-muted text-xs">{s.address || '—'}</TableCell>
                        <TableCell>
                          <button
                            type="button"
                            data-testid={`admin-send-nca-${s.id}`}
                            onClick={() => { setNcaDialogSupplier(s); setNcaDocType(s.type === 'agent' ? 'agent_nca' : 'supplier_nca'); }}
                            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-brand-amber border border-brand-border rounded-full px-3 py-1.5 hover:border-brand-amber transition-colors whitespace-nowrap"
                          >
                            <FileSignature className="w-3 h-3" />
                            Send NCA
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* AGREEMENTS */}
            <TabsContent value="agreements" className="mt-6">
              <div className="rounded-xl border border-brand-border overflow-hidden" data-testid="admin-agreements-table">
                <Table>
                  <TableHeader>
                    <TableRow className="border-brand-border hover:bg-transparent">
                      {['Recipient', 'Type', 'Status', 'Sent', 'Signed', 'Signature', 'PDF', 'Link'].map((h) => (
                        <TableHead key={h} className="text-brand-muted font-mono text-[11px] uppercase tracking-wider">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {agreements.length === 0 && (
                      <TableRow className="border-brand-border"><TableCell colSpan={8} className="text-center text-brand-muted py-8">No agreements sent yet.</TableCell></TableRow>
                    )}
                    {agreements.map((a) => (
                      <TableRow key={a.sign_token} className="border-brand-border hover:bg-brand-surface2/60">
                        <TableCell className="text-brand-text text-sm">{a.recipient_name}{a.recipient_company ? ` (${a.recipient_company})` : ''}</TableCell>
                        <TableCell className="text-brand-muted text-xs capitalize">{a.document_type.replace('_', ' ')}</TableCell>
                        <TableCell className={`text-xs uppercase ${a.status === 'signed' ? 'text-brand-green' : 'text-brand-amber'}`}>{a.status}</TableCell>
                        <TableCell className="text-brand-muted text-xs">{fmtDate(a.created_at)}</TableCell>
                        <TableCell className="text-brand-muted text-xs">{fmtDate(a.signed_at)}</TableCell>
                        <TableCell className="text-brand-muted text-xs">{a.signature_name || '—'}</TableCell>
                        <TableCell>
                          <a href={`${API}/agreements/${a.sign_token}/pdf`} target="_blank" rel="noopener noreferrer" className="text-brand-amber text-xs underline">View</a>
                        </TableCell>
                        <TableCell>
                          <button type="button" data-testid={`admin-copy-sign-link-${a.sign_token}`} onClick={() => copySignLink(a.sign_token)} className="text-brand-muted text-xs underline hover:text-brand-amber">
                            Copy Link
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* LEADS */}
            <TabsContent value="leads" className="mt-6">
              <div className="flex justify-end mb-3">
                <button type="button" data-testid="admin-export-leads-button" onClick={() => exportCSV(leads, `bulkusedtires-leads-${Date.now()}.csv`)} disabled={leads.length === 0} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-brand-muted border border-brand-border rounded-full px-4 py-2 hover:border-brand-amber hover:text-brand-amber transition-colors disabled:opacity-40">
                  <Download className="w-3.5 h-3.5" />
                  Export CSV
                </button>
              </div>
              <div className="rounded-xl border border-brand-border overflow-hidden" data-testid="admin-leads-table">
                <Table>
                  <TableHeader>
                    <TableRow className="border-brand-border hover:bg-transparent">
                      {['Name', 'Email', 'Phone', 'Company', 'Type', 'Date'].map((h) => (
                        <TableHead key={h} className="text-brand-muted font-mono text-[11px] uppercase tracking-wider">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.length === 0 && (
                      <TableRow className="border-brand-border"><TableCell colSpan={6} className="text-center text-brand-muted py-8">No leads yet.</TableCell></TableRow>
                    )}
                    {leads.map((l) => (
                      <TableRow key={`${l.email}-${l.created_at}`} className="border-brand-border hover:bg-brand-surface2/60">
                        <TableCell className="text-brand-text text-sm">{l.name}</TableCell>
                        <TableCell className="text-brand-muted text-xs">{l.email}</TableCell>
                        <TableCell className="text-brand-muted text-xs">{l.phone}</TableCell>
                        <TableCell className="text-brand-muted text-xs">{l.company || '—'}</TableCell>
                        <TableCell className="text-brand-muted text-xs">{l.company_type || '—'}</TableCell>
                        <TableCell className="text-brand-muted text-xs">{fmtDate(l.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </main>

      {/* Add Supplier/Agent Dialog */}
      <Dialog open={supplierDialogOpen} onOpenChange={setSupplierDialogOpen}>
        <DialogContent className="bg-brand-surface border-brand-border text-brand-text" data-testid="admin-supplier-dialog">
          <DialogHeader>
            <DialogTitle className="text-brand-text font-display uppercase">Add Supplier / Agent</DialogTitle>
            <DialogDescription className="text-brand-muted text-sm">
              Add a new supplier or agent to your network to route dropship quote requests to them.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddSupplier} className="space-y-4">
            <div>
              <Label className="text-brand-muted text-xs uppercase tracking-wider">Name *</Label>
              <Input data-testid="admin-supplier-name-input" value={supplierForm.name} onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
            </div>
            <div>
              <Label className="text-brand-muted text-xs uppercase tracking-wider">Company</Label>
              <Input data-testid="admin-supplier-company-input" value={supplierForm.company} onChange={(e) => setSupplierForm({ ...supplierForm, company: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-brand-muted text-xs uppercase tracking-wider">Email *</Label>
                <Input type="email" data-testid="admin-supplier-email-input" value={supplierForm.email} onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
              </div>
              <div>
                <Label className="text-brand-muted text-xs uppercase tracking-wider">Phone *</Label>
                <Input data-testid="admin-supplier-phone-input" value={supplierForm.phone} onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
              </div>
            </div>
            <div>
              <Label className="text-brand-muted text-xs uppercase tracking-wider">Type *</Label>
              <Select value={supplierForm.type} onValueChange={(v) => setSupplierForm({ ...supplierForm, type: v })}>
                <SelectTrigger data-testid="admin-supplier-type-select" className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="supplier">Supplier</SelectItem>
                  <SelectItem value="agent">Agent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-brand-muted text-xs uppercase tracking-wider">Mailing Address</Label>
              <Input data-testid="admin-supplier-address-input" value={supplierForm.address} onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
            </div>
            <DialogFooter>
              <button type="submit" disabled={savingSupplier} data-testid="admin-supplier-save-button" className="inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white font-display font-bold uppercase tracking-wide rounded-full px-6 py-2.5 transition-colors disabled:opacity-60">
                {savingSupplier ? 'Saving…' : 'Save'}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Send NCA Dialog */}
      <Dialog open={!!ncaDialogSupplier} onOpenChange={(open) => !open && setNcaDialogSupplier(null)}>
        <DialogContent className="bg-brand-surface border-brand-border text-brand-text" data-testid="admin-nca-dialog">
          <DialogHeader>
            <DialogTitle className="text-brand-text font-display uppercase">Send Non-Circumvention Agreement</DialogTitle>
            <DialogDescription className="text-brand-muted text-sm">
              Email a secure e-signature link so this supplier/agent can review and sign the NCA.
            </DialogDescription>
          </DialogHeader>
          {ncaDialogSupplier && (
            <>
              <p className="text-brand-muted text-sm">
                Sending to <strong className="text-brand-text">{ncaDialogSupplier.name}</strong> ({ncaDialogSupplier.email})
              </p>
              <div>
                <Label className="text-brand-muted text-xs uppercase tracking-wider">Document Type</Label>
                <Select value={ncaDocType} onValueChange={setNcaDocType}>
                  <SelectTrigger data-testid="admin-nca-doctype-select" className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="supplier_nca">Supplier Non-Circumvention Agreement</SelectItem>
                    <SelectItem value="agent_nca">Agent Non-Circumvention Agreement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <button type="button" disabled={sendingNca} onClick={handleSendNca} data-testid="admin-nca-send-button" className="inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white font-display font-bold uppercase tracking-wide rounded-full px-6 py-2.5 transition-colors disabled:opacity-60">
                  {sendingNca ? 'Sending…' : 'Send for Signature'}
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Send to Supplier Dialog */}
      <Dialog open={!!requestDialogOrder} onOpenChange={(open) => !open && setRequestDialogOrder(null)}>
        <DialogContent className="bg-brand-surface border-brand-border text-brand-text" data-testid="admin-request-dialog">
          <DialogHeader>
            <DialogTitle className="text-brand-text font-display uppercase">Request Supplier Quote</DialogTitle>
            <DialogDescription className="text-brand-muted text-sm">
              Route this order's details and delivery area to a supplier for a quote, without sharing buyer info.
            </DialogDescription>
          </DialogHeader>
          {requestDialogOrder && (
            <>
              <p className="text-brand-muted text-sm">
                Order <strong className="text-brand-amber">{requestDialogOrder.order_number}</strong> — {requestDialogOrder.tier} tires.
                The supplier will only see the order details and delivery area, never the buyer's name or contact info.
              </p>
              <div>
                <Label className="text-brand-muted text-xs uppercase tracking-wider">Select Supplier</Label>
                <Select value={requestSupplierId} onValueChange={setRequestSupplierId}>
                  <SelectTrigger data-testid="admin-request-supplier-select" className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text">
                    <SelectValue placeholder="Choose a supplier…" />
                  </SelectTrigger>
                  <SelectContent>
                    {supplierOptions.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}{s.company ? ` (${s.company})` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {supplierOptions.length === 0 && (
                  <p className="text-brand-muted text-xs mt-2">No suppliers on file yet — add one in the Suppliers tab first.</p>
                )}
              </div>
              <DialogFooter>
                <button type="button" disabled={sendingRequest} onClick={handleSendSupplierRequest} data-testid="admin-request-send-button" className="inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white font-display font-bold uppercase tracking-wide rounded-full px-6 py-2.5 transition-colors disabled:opacity-60">
                  {sendingRequest ? 'Sending…' : 'Send Quote Request'}
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
